import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Grid,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import ChartCard from '@components/charts/ChartCard';
import ScanRunFindings from '@components/findings/ScanRunFindings';
import { wcagLevelPanel, criteriaPanel, testsPanel } from '@utils/scanRunCharts';
import { useActiveOrgStore } from '@store/index';
import {
  testService,
  type ScanRunDetail,
  type ScanStatus,
  type TestResultItem,
} from '@services/testService';
import { toDisplayError } from '@utils/apiError';
import { runDensity } from '@utils/runDensity';
import { canTriageFindings } from '@utils/findingTriage';
import { organizationService } from '@services/organizationService';
import { RunSource } from '../components/runs/RunSource';
import { DiscoveryGuideLink, DiscoveryNotice } from '../components/runs/DiscoveryOutcome';

function statusChipColor(s: ScanStatus): 'default' | 'info' | 'success' | 'error' | 'warning' {
  if (s === 'completed') return 'success';
  if (s === 'running' || s === 'pending') return 'info';
  if (s === 'failed') return 'error';
  if (s === 'cancelled') return 'warning';
  return 'default';
}

/**
 * REV-498: these buckets were critical/serious/moderate/minor, which is not the
 * vocabulary anything else uses. The engine's Mongo TestResult
 * findingsSummary.bySeverity sub-document, FindingSeverity, and OrgFindingsPage
 * all use critical/major/minor/info — so the Serious/Moderate cards were reading
 * keys that never exist and permanently displayed 0.
 *
 * Caveat worth knowing: reading the right keys does not by itself make Major
 * appear for platform-run scans. TestResult.storeResult currently hardcodes
 * bySeverity.major = 0, so only info/minor/critical are populated there; the CI
 * ingest path does populate major. That inconsistency (MySQL FindingInstance
 * rows can be 'major' via _mapImpactToSeverity while the Mongo summary for the
 * same scan reports 0) is a backend bug tracked separately — not something this
 * page can fix.
 */
const SEVERITY_ORDER = ['critical', 'major', 'minor', 'info'] as const;
type Severity = (typeof SEVERITY_ORDER)[number];

const SEVERITY_COLOR: Record<Severity, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  major: 'warning',
  minor: 'info',
  info: 'default',
};

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
  info: 'Info',
};

/**
 * Aggregate severity counts across all per-target results. Ignores
 * unknown severity buckets — UI only renders the canonical four — but
 * still includes them in the total for consistency with the backend's
 * totalFindings.
 */
function aggregateBySeverity(results: TestResultItem[] | null): Record<Severity, number> {
  const acc: Record<Severity, number> = { critical: 0, major: 0, minor: 0, info: 0 };
  if (!results) return acc;
  for (const r of results) {
    const bs = r.summary?.bySeverity || {};
    for (const sev of SEVERITY_ORDER) {
      const n = bs[sev];
      if (typeof n === 'number') acc[sev] += n;
    }
  }
  return acc;
}

/**
 * REV-131 + REV-132: scan-run detail view. Shows status + URL + summary
 * stats (total findings, density score, per-severity counts) and links
 * into the org findings list for drill-down.
 */
export default function TestRunDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { activeOrg } = useActiveOrgStore();

  const mode = useTheme().palette.mode;
  const [detail, setDetail] = useState<ScanRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const r = await testService.getTest(id, activeOrg?.id);
      setDetail(r);
    } catch (err) {
      setError(
        toDisplayError(err, t('testRunDetail.errors.fetchFailed', 'Failed to load scan run'))
      );
    } finally {
      setLoading(false);
    }
  }, [id, activeOrg?.id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * REV-949: the caller's role in the active organization, read fresh by slug
   * as /findings reads it, decides whether bulk triage is offered. The role the
   * switcher persisted is not used: it is refreshed only on switching
   * organizations, so it outlives a role change. Null until known, and on
   * failure, which offers none (the API refuses a viewer either way).
   *
   * REV-951: a failure still offers none, but no longer silently — a member
   * whose lookup failed would otherwise just find bulk triage missing.
   */
  const [orgRole, setOrgRole] = useState<string | null>(null);
  const [roleLookupFailed, setRoleLookupFailed] = useState(false);
  const activeSlug = activeOrg?.slug;
  useEffect(() => {
    setOrgRole(null);
    setRoleLookupFailed(false);
    if (!activeSlug) return;
    let cancelled = false;
    organizationService
      .getBySlug(activeSlug)
      .then((org) => {
        if (!cancelled) setOrgRole(org.role ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setOrgRole(null);
        setRoleLookupFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [activeSlug]);

  const panelLabels = useMemo(
    () => ({
      level: t('testRunDetail.charts.wcagLevel', 'Findings by WCAG level'),
      criteria: t('testRunDetail.charts.criteria', 'Success criteria failed'),
      tests: t('testRunDetail.charts.tests', 'Tests failed'),
      findings: t('testRunDetail.charts.findings', 'Findings'),
      criterion: t('testRunDetail.charts.criterion', 'Success criterion'),
      rule: t('testRunDetail.charts.rule', 'Test'),
      showTable: t('testRunDetail.charts.showData', 'Show data table'),
      // Said out loud rather than left to the reader to notice: the chart
      // draws the top slice, the table beside it has everything.
      topOf: (shown: number, total: number) =>
        t('testRunDetail.charts.topOf', 'top {{shown}} of {{total}}', { shown, total }),
      unspecified: t('testRunDetail.charts.unspecified', 'Unspecified'),
    }),
    [t]
  );

  const severityCounts = useMemo(
    () => aggregateBySeverity(detail?.results ?? null),
    [detail?.results]
  );

  // Findings per page, or '—' for a run that has none (see runDensity).
  const densityScore = useMemo(() => (detail ? runDensity(detail.scanRun) : null), [detail]);

  if (loading && !detail) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !detail) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackIcon />} component={RouterLink} to="/tests/new">
          {t('testRunDetail.backToRun', 'Back to Run a test')}
        </Button>
      </Box>
    );
  }

  if (!detail) return null;
  const { scanRun: run } = detail;
  const isComplete = run.status === 'completed';

  // REV-732: the destination is derived once, named, and passed as an href.
  // It was previously built inline inside an onClick, which is where the
  // line-based sweep could not see it.
  const findingsHref = activeOrg
    ? `/orgs/${activeOrg.slug}/findings${run.contextId ? `?context_id=${run.contextId}` : ''}`
    : '';

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} component={RouterLink} to="/tests/new" sx={{ mb: 2 }}>
        {t('testRunDetail.backToRun', 'Back to Run a test')}
      </Button>

      <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
        {t('testRunDetail.title', 'Scan run')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-all' }}>
        {run.targetUrl}
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            color={statusChipColor(run.status)}
            label={t(`testRunDetail.status.${run.status}`, run.status)}
          />
          <Typography variant="body2" color="text.secondary">
            {t('testRunDetail.progress', {
              defaultValue: '{{done}} of {{total}} targets',
              done: run.completedTargets,
              total: run.totalTargets,
            })}
          </Typography>
          {run.isEphemeral && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={t('testRunDetail.ephemeral', 'Ephemeral (limited retention)')}
            />
          )}
        </Stack>
        {run.errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {run.errorMessage}
            {/* REV-1145: the message names the allowlisting guide; link it. */}
            {run.discovery?.outcome === 'blocked' && <DiscoveryGuideLink />}
          </Alert>
        )}
        {/* REV-944: a crawl that bot protection partly blocked. A fully
            blocked run is failed, and its error message above says so. */}
        <DiscoveryNotice discovery={run.discovery} />
        {/* REV-813: commit, branch and pull request, for a run from CI. */}
        <RunSource source={run.source} />
      </Paper>

      {isComplete && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            mb: 2,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(6, 1fr)',
            },
          }}
        >
          <StatCard
            label={t('testRunDetail.stats.totalFindings', 'Total findings')}
            value={run.totalFindings}
            testid="stat-total"
          />
          <StatCard
            label={t('testRunDetail.stats.density', 'Findings / page')}
            // REV-939: a whole number reads as one ("181", not "181.0");
            // anything else keeps one decimal place. Round FIRST, then drop the
            // ".0": 3.96 is not an integer but displays as "4.0".
            value={densityScore == null ? '—' : densityScore.toFixed(1).replace(/\.0$/, '')}
            testid="stat-density"
          />
          {SEVERITY_ORDER.map((sev) => (
            <StatCard
              key={sev}
              label={t(`testRunDetail.stats.${sev}`, SEVERITY_LABEL[sev])}
              value={severityCounts[sev]}
              color={SEVERITY_COLOR[sev]}
              testid={`stat-${sev}`}
            />
          ))}
        </Box>
      )}

      {/* REV-711: the walkthrough found this page showed counts and no
          visualisation. Three panels: which conformance levels the findings
          sit under, which success criteria failed hardest, and which tests
          fired most. The aggregation is server-side — the rule and WCAG
          catalogs live in revenant-api and resolving them per finding from
          here would be an N+1 across the wire. */}
      {isComplete && detail.aggregates && detail.scanRun.totalFindings > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            wcagLevelPanel(detail.aggregates, mode, panelLabels),
            criteriaPanel(detail.aggregates, mode, panelLabels),
            testsPanel(detail.aggregates, mode, panelLabels),
          ].map((panel) => (
            <ChartCard
              key={panel.title}
              {...panel}
              emptyText={t(
                'testRunDetail.charts.empty',
                'No findings in this run map to one of these.'
              )}
            />
          ))}
        </Grid>
      )}

      {/* REV-498 put the per-target issues on this page; REV-720 makes them the
          SAME table /findings renders, reading the same endpoint. What used to
          live here was a second implementation of that list, with its own
          columns and its own severity labels — which is how the two came to
          disagree about what a finding is. */}
      {/* REV-951: only where triage would have been offered — a registered run
          in an organization. An unregistered run offers none whatever the
          role, so saying the role hid it would be untrue. */}
      {isComplete && roleLookupFailed && activeOrg && run.contextId && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t(
            'testRunDetail.roleUnknown',
            "Couldn't confirm your role, so triage actions are hidden. Reload to try again."
          )}
        </Typography>
      )}
      {isComplete && (
        <ScanRunFindings
          scanRunId={run.id}
          contextId={run.contextId ?? null}
          organizationId={activeOrg?.id}
          canTriage={canTriageFindings(orgRole)}
          orgSlug={activeOrg?.slug}
          results={detail.results}
          // REV-715: grouped by content type, then rule. Also the rule
          // filter's options, which are every rule the run failed.
          groups={detail.aggregates?.byCategory.categories}
        />
      )}

      {activeOrg && isComplete && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            endIcon={<OpenInNewIcon />}
            component={RouterLink}
            to={findingsHref}
          >
            {/* REV-512: the link already carried ?context_id when the run
                belonged to a tracked place, but the label said "for this org"
                — and the destination ignored the param, so it really did show
                the whole org. Now the param is honoured, the label has to tell
                the truth about where you are going. */}
            {run.contextId
              ? t('testRunDetail.viewFindingsForPlace', 'View findings for this tracked place')
              : t('testRunDetail.viewFindings', 'View findings for this org')}
          </Button>
          <Button variant="text" component={RouterLink} to="/tests/new">
            {t('testRunDetail.runAnother', 'Run another')}
          </Button>
        </Stack>
      )}
    </Box>
  );
}

function StatCard({
  label,
  value,
  color = 'default',
  testid,
}: {
  label: string;
  value: number | string;
  color?: 'error' | 'warning' | 'info' | 'default';
  testid?: string;
}) {
  const valueColor =
    color === 'default'
      ? 'text.primary'
      : color === 'error'
        ? 'error.main'
        : color === 'warning'
          ? 'warning.main'
          : 'info.main';
  return (
    <Card variant="outlined" data-testid={testid}>
      <CardContent sx={{ textAlign: 'center', '&:last-child': { pb: 2 } }}>
        <Typography variant="h4" component="p" fontWeight={700} color={valueColor}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}
